'use client';

import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';

import { isChunkLoadError, recoverFromChunkError } from '@/src/utils/chunkLoadError';
import { existeVersaoNova } from '@/src/utils/staleDeployment';

/**
 * Error boundary de nível de aplicação.
 *
 * Captura qualquer exceção de render lançada em qualquer rota abaixo do
 * layout raiz (área autenticada, páginas full-page, admin). Substitui a tela
 * genérica "Application error..." por uma tela amigável e recuperável.
 *
 * Boundaries mais específicos (ex.: `Demonstracoes/uploadPage/error.tsx`)
 * continuam tendo prioridade nos seus segmentos.
 */
export default function AppErrorBoundary({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    // Enquanto confere se há versão nova, não mostra a tela de erro (evita o
    // "pisca" antes do reload automático).
    const [verificando, setVerificando] = useState(true);

    useEffect(() => {
        let ativo = true;
        setVerificando(true);
        // Falha de chunk (deploy novo + cache antigo): recarrega 1x para
        // buscar o HTML atualizado em vez de mostrar o erro.
        if (isChunkLoadError(error) && recoverFromChunkError()) {
            return;
        }
        // Aba com build antigo pode falhar com outras mensagens; se o servidor
        // já está em outra versão, recarrega em vez de mostrar o erro.
        existeVersaoNova().then((novaVersao) => {
            if (!ativo) return;
            if (novaVersao && recoverFromChunkError()) {
                Sentry.addBreadcrumb({ category: 'stale-deploy', message: 'reload por buildId divergente' });
                return;
            }
            Sentry.captureException(error);
            setVerificando(false);
        });
        return () => {
            ativo = false;
        };
    }, [error]);

    if (verificando) {
        return (
            <div className="surface-ground min-h-screen flex align-items-center justify-content-center p-4">
                <span className="text-600">
                    <i className="pi pi-spin pi-spinner mr-2" />
                    Carregando...
                </span>
            </div>
        );
    }

    return (
        <div className="surface-ground min-h-screen flex align-items-center justify-content-center p-4">
            <div className="surface-card border-1 surface-border border-round p-4" style={{ maxWidth: 520 }}>
                <div className="flex align-items-center gap-2 mb-3">
                    <i className="pi pi-exclamation-triangle text-red-500" />
                    <h2 className="m-0 text-xl">Não foi possível carregar esta tela</h2>
                </div>
                <p className="text-700 mt-0">
                    A aplicação encontrou uma falha inesperada, mas continua ativa. Tente novamente ou recarregue a página.
                </p>
                <div className="flex gap-2 flex-wrap">
                    <button type="button" className="p-button p-component" onClick={() => reset()}>
                        <span className="p-button-label">Tentar novamente</span>
                    </button>
                    <button
                        type="button"
                        className="p-button p-component p-button-outlined"
                        onClick={() => window.location.reload()}
                    >
                        <span className="p-button-label">Recarregar página</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
